import React, { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "features/config/hooks";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import {
  FaStore,
  FaCheckCircle,
  FaTimesCircle,
  FaCalendarAlt,
  FaChartLine,
  FaLock,
} from "react-icons/fa";
import { MdStorefront, MdTrendingUp, MdTrendingDown } from "react-icons/md";
import {
  getDashboardData,
  getStoreStatusByDate,
  selectStoreStatus,
  StoreStatusState,
} from "../slices/store-status.slice";

// Helper function to format date for API
const formatDateForAPI = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`; // yyyy-mm-dd in local timezone
};

// Helper function to format API date for display
const formatAPIDateToDisplay = (apiDate: string): string => {
  const date = new Date(apiDate + "T00:00:00");
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

export function StoreStatusDashboard() {
  const dispatch = useAppDispatch();
  const storeStatusState = useAppSelector(selectStoreStatus);

  // FIXED: All hooks must be called at the top level
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        await dispatch(getDashboardData());
        const todayAPI = formatDateForAPI(new Date());
        console.log("Dashboard loading date (API):", todayAPI);
        await dispatch(getStoreStatusByDate({ date: todayAPI }));
      } catch (error) {
        console.log("Dashboard data loading completed with some empty results");
        // Don't show error for empty data
      }
    };

    loadDashboardData();
  }, [dispatch]);

  // FIXED: Handle permission check after all hooks are called
  if (storeStatusState.permissionError) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <Card>
          <CardContent className="text-center py-12">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaLock className="text-red-500" size={24} />
            </div>
            <Typography variant="h5" className="text-gray-800 mb-2">
              Access Denied
            </Typography>
            <Typography variant="body1" className="text-gray-600 mb-4">
              {storeStatusState.permissionError}
            </Typography>
            <Typography variant="body2" color="textSecondary" className="mb-4">
              Only Team Captains, Managers, and Admins can access Store Status
              Dashboard.
            </Typography>
            <Button variant="outlined" onClick={() => window.history.back()}>
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (storeStatusState.status === StoreStatusState.inProgress) {
    return (
      <Box className="min-h-screen bg-gray-50 flex items-center justify-center">
        <CircularProgress />
      </Box>
    );
  }

  // if (storeStatusState.status === StoreStatusState.fail) {
  //   return (
  //     <Box className="min-h-screen bg-gray-50 p-6">
  //       <Alert severity="error">{storeStatusState.message}</Alert>
  //     </Box>
  //   );
  // }

  const stats = storeStatusState.dashboardStats;
  const todayStatuses = storeStatusState.storeStatuses;

  // Process today's stats
  const todayOpen =
    stats?.today_stats?.find((s) => s.status === "open")?.count || 0;
  const todayClosed =
    stats?.today_stats?.find((s) => s.status === "closed")?.count || 0;
  const totalStores = todayOpen + todayClosed;

  // Process weekly trend for chart display
  const weeklyData = stats?.weekly_trend || [];
  const processedWeeklyData = weeklyData.reduce((acc, curr) => {
    const date = curr.status_date;
    if (!acc[date]) {
      acc[date] = { date, open: 0, closed: 0 };
    }
    acc[date][curr.status as "open" | "closed"] = curr.count;
    return acc;
  }, {} as Record<string, { date: string; open: number; closed: number }>);

  const weeklyTrend = Object.values(processedWeeklyData);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
            <FaStore className="text-white" size={20} />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Store Status Dashboard
            </h1>
            <p className="text-gray-600">
              Monitor daily store open/closed status
            </p>
          </div>
        </div>
      </div>

      <Grid container spacing={3}>
        {/* Quick Stats */}
        <Grid item xs={12} md={3}>
          <Card className="h-full">
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <Typography variant="h4" className="font-bold text-gray-900">
                    {totalStores}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Stores Today
                  </Typography>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <MdStorefront className="text-blue-600" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card className="h-full">
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <Typography variant="h4" className="font-bold text-green-600">
                    {todayOpen}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Stores Open
                  </Typography>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <FaCheckCircle className="text-green-600" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card className="h-full">
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <Typography variant="h4" className="font-bold text-red-600">
                    {todayClosed}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Stores Closed
                  </Typography>
                </div>
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                  <FaTimesCircle className="text-red-600" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card className="h-full">
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <Typography variant="h4" className="font-bold text-gray-900">
                    {totalStores > 0
                      ? Math.round((todayOpen / totalStores) * 100)
                      : 0}
                    %
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Open Rate
                  </Typography>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <FaChartLine className="text-orange-600" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>
        </Grid>

        {/* Weekly Trend */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" className="mb-4 font-semibold">
                Weekly Trend
              </Typography>
              <div className="space-y-3">
                {weeklyTrend.map((day, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center space-x-3">
                      <FaCalendarAlt className="text-gray-500" size={16} />
                      <Typography variant="body2" className="font-medium">
                        {formatAPIDateToDisplay(day.date)}
                      </Typography>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <Typography
                          variant="body2"
                          className="text-green-600 font-medium"
                        >
                          {day.open} Open
                        </Typography>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                        <Typography
                          variant="body2"
                          className="text-red-600 font-medium"
                        >
                          {day.closed} Closed
                        </Typography>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </Grid>

        {/* Today's Store Status */}
        <Grid item xs={12} md={4}>
          <Card className="h-full">
            <CardContent>
              <Typography variant="h6" className="mb-4 font-semibold">
                Today's Status ({new Date().toLocaleDateString("en-US")})
              </Typography>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {todayStatuses.length > 0 ? (
                  todayStatuses.map((status) => (
                    <div key={status.id} className="p-3 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <Typography variant="body2" className="font-medium">
                          {status.store_name}
                        </Typography>
                        <Chip
                          label={status.status.toUpperCase()}
                          color={status.status === "open" ? "success" : "error"}
                          size="small"
                        />
                      </div>
                      <Typography variant="caption" color="textSecondary">
                        {status.address}
                      </Typography>
                      {status.notes && (
                        <Typography
                          variant="caption"
                          className="block mt-1 text-gray-600"
                        >
                          Note: {status.notes}
                        </Typography>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <FaStore size={32} className="mx-auto mb-2 opacity-50" />
                    <Typography variant="body2">
                      No status data for today
                    </Typography>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
}
